/**
 * #978 — the card-note controller: records, the single open editor, and the
 * mounted-control registry, for ONE scope at a time.
 *
 * One instance per `CardNotesProvider`. It is deliberately not a module-level
 * store: the page that mounts the provider owns its lifetime, so an unmounted
 * page (or the normal route, which mounts none) holds no note state at all.
 *
 * ─── Invariants ───────────────────────────────────────────────────────────────
 *
 * - SCOPE ISOLATION. `setScope` bumps `generation` and drops every record and
 *   the open editor, draft included. Every async result compares the generation
 *   it started under, so a late read or save from the old account/org can never
 *   populate the new one (contract §1, "late responses cannot populate the new
 *   context").
 * - NO TICK TRAFFIC. Reads are issued only when a control for a never-seen
 *   target mounts, batched per microtask in bounded chunks. Re-renders from
 *   market ticks do not re-register, so they cost no network call.
 * - A READ NEVER OVERWRITES A WRITE. Read results only fill entries still
 *   `loading`; a save's canonical record is authoritative once it lands.
 * - NO PREMATURE "SAVED". The saved indicator derives from `entries`, which a
 *   save only touches with the record the adapter returned.
 * - FOREIGN SCOPE IS INERT. A control that rendered a stale scope (it mounted
 *   in the same commit as a switch) is ignored by `registerAnchor` and `open`:
 *   no presence, no read, so old-scope identifiers never go over the wire.
 * - FRESH ON LOOK, NOT ON TICK. Opening a note re-reads that one target and
 *   keeps the result only if its revision is newer; nothing else polls.
 * - EXACT TARGET. The editor captures its target at open; nothing ever points
 *   a draft at a different target. "Target gone" means no mounted control for
 *   it — conservative by design: a filter hiding the card also pauses Save
 *   until the card is back, rather than saving blind.
 */
import { createStore, type StoreApi } from 'zustand/vanilla';
import { cardTargetIdentityValid } from '../cardAnnotations/identity';
import { isOverLimit, noteForSave } from './noteText';
import {
  CardNoteSaveError,
  targetKey,
  type CardNoteAdapter,
  type CardNoteRecord,
  type CardNoteTarget,
} from './types';

/** Upper bound on one `adapter.read` call. */
export const READ_BATCH_SIZE = 50;

export type NoteEntry =
  | { status: 'loading' }
  | { status: 'ready'; record: CardNoteRecord }
  | { status: 'error' }
  /** #976: the server could not tie this card to an account. Sticky until `setScope`. */
  | { status: 'unavailable' };

export type EditorIssue =
  | { kind: 'failed' }
  | { kind: 'forbidden' }
  | { kind: 'conflict'; latest: CardNoteRecord };

export interface NoteEditor {
  target: CardNoteTarget;
  key: string;
  /** The control instance that opened the editor — anchor + focus return. */
  originAnchorId: string;
  mode: 'view' | 'edit';
  saving: boolean;
  draft: string;
  /** Saved text the draft started from; dirty = draft differs from it. */
  baseNote: string;
  baseRevision: number;
  confirmingDiscard: boolean;
  issue: EditorIssue | null;
}

export interface CardNotesState {
  generation: number;
  /** null = no resolved identity; every control renders disabled. */
  scope: string | null;
  entries: Readonly<Record<string, NoteEntry>>;
  /** Mounted control count per target key. */
  presence: Readonly<Record<string, number>>;
  editor: NoteEditor | null;
}

/**
 * Dirty = Save would change the stored note. Compared after `noteForSave`, so
 * edge whitespace (which Save trims away) is neither a pointless write nor a
 * reason to ask before discarding.
 */
export function isDirty(editor: NoteEditor | null): boolean {
  return (
    editor != null &&
    editor.mode === 'edit' &&
    noteForSave(editor.draft) !== noteForSave(editor.baseNote)
  );
}

function emptyRecord(target: CardNoteTarget): CardNoteRecord {
  return { target, note: null, revision: 0, updatedAt: null };
}

export interface CardNotesController {
  store: StoreApi<CardNotesState>;
  setScope(scope: string | null): void;
  /** A control mounted. Returns its cleanup. */
  registerAnchor(anchorId: string, target: CardNoteTarget, el: HTMLElement): () => void;
  anchorElement(anchorId: string): HTMLElement | null;
  /** Any still-mounted control for `key` (the origin may be gone). */
  anyAnchorFor(key: string): HTMLElement | null;
  /**
   * Open the panel for `target`. Refused (returns false, raises the discard
   * choice) while ANOTHER target has unsaved text — a click elsewhere must
   * never silently throw a draft away.
   */
  open(target: CardNoteTarget, anchorId: string): boolean;
  startEdit(): void;
  setDraft(text: string): void;
  cancelEdit(): void;
  /** Close unless dirty; dirty raises the discard choice. True if closed. */
  requestClose(): boolean;
  keepEditing(): void;
  discardAndClose(): void;
  save(): Promise<void>;
  /** Conflict: keep my text, write it over the latest revision. */
  saveOverLatest(): Promise<void>;
  retryRead(key: string): void;
  /**
   * #976: the adapter reported these targets as not provable for the caller.
   * Foreign-scope targets (a late report after `setScope`) are ignored.
   */
  markUnavailable(targets: readonly CardNoteTarget[]): void;
}

export function createCardNotesController(
  adapter: CardNoteAdapter,
  initialScope: string | null,
): CardNotesController {
  const store = createStore<CardNotesState>(() => ({
    generation: 0,
    scope: initialScope,
    entries: {},
    presence: {},
    editor: null,
  }));
  const anchors = new Map<string, { el: HTMLElement; key: string }>();
  let pending = new Map<string, CardNoteTarget>();
  let flushScheduled = false;

  const patchEditor = (patch: Partial<NoteEditor>) => {
    const { editor } = store.getState();
    if (editor) store.setState({ editor: { ...editor, ...patch } });
  };

  const setEntries = (updates: Record<string, NoteEntry>) =>
    store.setState((s) => ({ entries: { ...s.entries, ...updates } }));

  const flush = () => {
    flushScheduled = false;
    const all = [...pending.values()];
    pending = new Map();
    const generation = store.getState().generation;
    // A target the server would refuse by identity must not ride in a batch: one bad id
    // fails the whole read. Show it unavailable here and send only the valid ones.
    const batch = all.filter(cardTargetIdentityValid);
    if (batch.length < all.length) {
      const bad: Record<string, NoteEntry> = {};
      for (const t of all) if (!cardTargetIdentityValid(t)) bad[targetKey(t)] = { status: 'unavailable' };
      setEntries(bad);
    }
    for (let i = 0; i < batch.length; i += READ_BATCH_SIZE) {
      const chunk = batch.slice(i, i + READ_BATCH_SIZE);
      adapter.read(chunk).then(
        (records) => {
          if (store.getState().generation !== generation) return;
          const byKey = new Map(records.map((r) => [targetKey(r.target), r]));
          const { entries } = store.getState();
          const updates: Record<string, NoteEntry> = {};
          for (const t of chunk) {
            const key = targetKey(t);
            if (entries[key]?.status !== 'loading') continue;
            updates[key] = { status: 'ready', record: byKey.get(key) ?? emptyRecord(t) };
          }
          setEntries(updates);
        },
        () => {
          if (store.getState().generation !== generation) return;
          const { entries } = store.getState();
          const updates: Record<string, NoteEntry> = {};
          for (const t of chunk) {
            const key = targetKey(t);
            if (entries[key]?.status === 'loading') updates[key] = { status: 'error' };
          }
          setEntries(updates);
        },
      );
    }
  };

  const requestRead = (target: CardNoteTarget) => {
    const key = targetKey(target);
    setEntries({ [key]: { status: 'loading' } });
    pending.set(key, target);
    if (!flushScheduled) {
      flushScheduled = true;
      queueMicrotask(flush);
    }
  };

  /** One-target re-check on open. A failure keeps the cached record. */
  const revalidate = (target: CardNoteTarget) => {
    const key = targetKey(target);
    const generation = store.getState().generation;
    adapter.read([target]).then(
      (records) => {
        if (store.getState().generation !== generation) return;
        const fresh = records.find((r) => targetKey(r.target) === key);
        const current = store.getState().entries[key];
        if (!fresh || current?.status !== 'ready' || fresh.revision <= current.record.revision) return;
        setEntries({ [key]: { status: 'ready', record: fresh } });
      },
      () => {},
    );
  };

  const inScope = (target: CardNoteTarget) => target.scope === store.getState().scope;

  const saveWith = async (expectedRevision: number) => {
    const { editor, generation, presence } = store.getState();
    if (!editor || editor.mode !== 'edit' || editor.saving) return;
    // Target gone: keep the draft for copying, refuse to write anywhere.
    if (!presence[editor.key]) return;
    if (isOverLimit(editor.draft)) return;
    const { target, key } = editor;
    // #976: the card turned unavailable while the draft was open. The draft stays (to copy), but
    // nothing is sent: the server would refuse it.
    if (store.getState().entries[key]?.status === 'unavailable') {
      patchEditor({ issue: { kind: 'forbidden' } });
      return;
    }
    patchEditor({ saving: true, issue: null, confirmingDiscard: false });
    try {
      const saved = await adapter.save(target, noteForSave(editor.draft), expectedRevision);
      if (store.getState().generation !== generation) return;
      setEntries({ [key]: { status: 'ready', record: saved } });
      const current = store.getState().editor;
      if (current?.key === key) {
        patchEditor({ mode: 'view', saving: false, draft: '', baseNote: '', issue: null, confirmingDiscard: false });
      }
    } catch (err) {
      if (store.getState().generation !== generation) return;
      if (err instanceof CardNoteSaveError && err.reason === 'conflict' && err.latest) {
        // The server's record is real state — show it — but the draft stays.
        setEntries({ [key]: { status: 'ready', record: err.latest } });
        patchEditor({ saving: false, issue: { kind: 'conflict', latest: err.latest } });
      } else if (err instanceof CardNoteSaveError && err.reason === 'forbidden') {
        patchEditor({ saving: false, issue: { kind: 'forbidden' } });
      } else {
        patchEditor({ saving: false, issue: { kind: 'failed' } });
      }
    }
  };

  const close = () => store.setState({ editor: null });

  return {
    store,

    setScope(scope) {
      if (store.getState().scope === scope) return;
      pending = new Map();
      store.setState((s) => ({
        generation: s.generation + 1,
        scope,
        entries: {},
        editor: null,
      }));
    },

    registerAnchor(anchorId, target, el) {
      if (!inScope(target)) return () => {};
      const key = targetKey(target);
      anchors.set(anchorId, { el, key });
      store.setState((s) => ({ presence: { ...s.presence, [key]: (s.presence[key] ?? 0) + 1 } }));
      if (!store.getState().entries[key]) requestRead(target);
      return () => {
        if (anchors.get(anchorId)?.el === el) anchors.delete(anchorId);
        // The clicked copy left while another copy of the card remains (its
        // section vs the Pinned strip): hand the editor to the survivor so the
        // panel, focus return and outside-click all follow a live element.
        const { editor } = store.getState();
        if (editor?.originAnchorId === anchorId) {
          const survivor = [...anchors].find(([, a]) => a.key === key)?.[0];
          if (survivor) patchEditor({ originAnchorId: survivor });
        }
        store.setState((s) => {
          const presence = { ...s.presence };
          const n = (presence[key] ?? 1) - 1;
          if (n > 0) presence[key] = n;
          else delete presence[key];
          return { presence };
        });
      };
    },

    anchorElement(anchorId) {
      return anchors.get(anchorId)?.el ?? null;
    },

    anyAnchorFor(key) {
      for (const a of anchors.values()) if (a.key === key) return a.el;
      return null;
    },

    open(target, anchorId) {
      if (!inScope(target)) return false;
      const { editor, entries } = store.getState();
      const key = targetKey(target);
      if (editor?.key === key) {
        patchEditor({ originAnchorId: anchorId });
        return true;
      }
      // Mid-save the text is already on its way; a prompt here would hide the
      // Saving… state behind a Discard that cannot act. Ignore the click.
      if (editor?.saving) return false;
      if (isDirty(editor)) {
        patchEditor({ confirmingDiscard: true });
        return false;
      }
      const status = entries[key]?.status;
      if (status === 'unavailable') return false;
      if (status === 'error') requestRead(target);
      else if (status === 'ready') revalidate(target);
      store.setState({
        editor: {
          target,
          key,
          originAnchorId: anchorId,
          mode: 'view',
          saving: false,
          draft: '',
          baseNote: '',
          baseRevision: 0,
          confirmingDiscard: false,
          issue: null,
        },
      });
      return true;
    },

    startEdit() {
      const { editor, entries } = store.getState();
      if (!editor || editor.mode === 'edit') return;
      const entry = entries[editor.key];
      if (entry?.status !== 'ready') return;
      const base = entry.record.note ?? '';
      patchEditor({
        mode: 'edit',
        draft: base,
        baseNote: base,
        baseRevision: entry.record.revision,
        issue: null,
        confirmingDiscard: false,
      });
    },

    setDraft(text) {
      const { editor } = store.getState();
      if (!editor || editor.mode !== 'edit' || editor.saving) return;
      patchEditor({ draft: text, confirmingDiscard: false });
    },

    cancelEdit() {
      const { editor } = store.getState();
      if (!editor || editor.saving) return;
      patchEditor({ mode: 'view', draft: '', baseNote: '', issue: null, confirmingDiscard: false });
    },

    requestClose() {
      const { editor } = store.getState();
      if (!editor) return true;
      if (editor.saving) return false;
      if (isDirty(editor)) {
        patchEditor({ confirmingDiscard: true });
        return false;
      }
      close();
      return true;
    },

    keepEditing() {
      patchEditor({ confirmingDiscard: false });
    },

    discardAndClose() {
      if (store.getState().editor?.saving) return;
      close();
    },

    save() {
      const { editor } = store.getState();
      // Only the explicit "save mine over it" may write past a conflict.
      if (editor?.issue?.kind === 'conflict') return Promise.resolve();
      if (!isDirty(editor)) return Promise.resolve();
      return saveWith(editor?.baseRevision ?? 0);
    },

    saveOverLatest() {
      const { editor } = store.getState();
      if (editor?.issue?.kind !== 'conflict') return Promise.resolve();
      const latest = editor.issue.latest;
      patchEditor({ baseRevision: latest.revision, baseNote: latest.note ?? '' });
      // The other writer already saved exactly this text: nothing to write.
      if (!isDirty(store.getState().editor)) {
        patchEditor({ mode: 'view', draft: '', baseNote: '', issue: null });
        return Promise.resolve();
      }
      return saveWith(latest.revision);
    },

    retryRead(key) {
      const { editor, entries } = store.getState();
      if (entries[key]?.status === 'unavailable') return;
      if (editor?.key === key) requestRead(editor.target);
    },

    markUnavailable(targets) {
      const updates: Record<string, NoteEntry> = {};
      for (const t of targets) {
        if (!inScope(t)) continue;
        updates[targetKey(t)] = { status: 'unavailable' };
      }
      if (Object.keys(updates).length > 0) setEntries(updates);
    },
  };
}
